import { AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, OnChanges, QueryList, SimpleChanges, ViewChildren, OnInit } from '@angular/core';
import { ReportsService } from '../../reports.service';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { InbuiltReportModel, PageModel, TabModel } from '../../inbuilt-report-v2/Inbuilt-report-model';
import { InbuiltUtilityService } from '../inbuilt-utility.service';
import { Subject, fromEvent, startWith, takeUntil, auditTime } from 'rxjs';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-inbuilt-report-v2',
  templateUrl: './inbuilt-report-v2.component.html',
  styleUrls: ['./inbuilt-report-v2.component.css'],
  standalone: false
})

export class InbuiltReportV2Component implements AfterViewInit, OnDestroy, OnChanges, OnInit {
  @ViewChildren('widgetHost', { read: ElementRef }) widgetHosts!: QueryList<ElementRef<HTMLElement>>;
  @Input() reportNameFromInput: string = '';
  reportNameFromQueryParams: string = '';
  initialDataLoaded: boolean = false;
  showView: boolean = false;
  searchTriggered: boolean = false;
  pollTimer = null;
  
  isCurrentPageDataLoaded: boolean = false;
  isParentReportDataLoaded: boolean = false;
  isChildReportDataLoaded: boolean = false;
  isPageChanging: boolean = false;
  isTabLabelEmpty: boolean = false;
  pages: Array<PageModel> = [];
  currentPage: PageModel | null = null;
  currentTab: TabModel | null = null;
  tableConfig: any = null;
  pageConfig: any = null;
  reportDocumentation: string = '';
  private readonly destroy$ = new Subject<void>();
  private resizeObserver: ResizeObserver | null = null;
  private resizeRafId: number | null = null;
  private isApplyingRowHeights = false;
  private readonly rowTopTolerance = 3;
  private readonly meaningfulHeightPx = 36;
  private readonly responsiveColClassMap: Record<number, string> = {
    1: 'col-12 col-sm-12 col-md-1 col-lg-1 col-xl-1 col-xxl-1',
    2: 'col-12 col-sm-12 col-md-2 col-lg-2 col-xl-2 col-xxl-2',
    3: 'col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3 col-xxl-3',
    4: 'col-12 col-sm-12 col-md-4 col-lg-4 col-xl-4 col-xxl-4',
    5: 'col-12 col-sm-12 col-md-5 col-lg-5 col-xl-5 col-xxl-5',
    6: 'col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6',
    7: 'col-12 col-sm-12 col-md-7 col-lg-7 col-xl-7 col-xxl-7',
    8: 'col-12 col-sm-12 col-md-8 col-lg-8 col-xl-8 col-xxl-8',
    9: 'col-12 col-sm-12 col-md-9 col-lg-9 col-xl-9 col-xxl-9',
    10: 'col-12 col-sm-12 col-md-10 col-lg-10 col-xl-10 col-xxl-10',
    11: 'col-12 col-sm-12 col-md-11 col-lg-11 col-xl-11 col-xxl-11',
    12: 'col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12'
  };

  constructor(
    private reportsService: ReportsService,
    private activeRoute: ActivatedRoute,
    private toastr: ToastrService,
    private inbuiltUtilityService: InbuiltUtilityService,
    private ngZone: NgZone,
    private sanitizer: DomSanitizer
  ) { }

  private get resolvedReportName(): string {
    return this.reportNameFromInput || this.reportNameFromQueryParams || '';
  }


  ngOnInit(): void {
    this.activeRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.reportNameFromQueryParams = params['ReportName'] || ''; //get report name
      if (this.resolvedReportName) {
        this.initialiseData();//initialise data based on report name
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reportNameFromInput'] && !changes['reportNameFromInput'].firstChange) {
      this.initialiseData();
    }
  }


  ngAfterViewInit(): void {
    this.widgetHosts.changes
      .pipe(startWith(this.widgetHosts), takeUntil(this.destroy$))
      .subscribe(() => {
        this.clearWidgetHeights();
        this.rebindResizeObservers();
        this.scheduleStabilizedRowSync();
      });

    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(auditTime(120), takeUntil(this.destroy$))
        .subscribe(() => {
          this.scheduleRowHeightSync();
        });
    });
  }

  initialiseData() {
    this.setDefaultData();
    this.showDefaultLoader();
    this.getParentJSON();
  }

  showDefaultLoader() {
    const intervalId = setInterval(() => {
      if (this.isParentReportDataLoaded) {
        if (this.isChildReportDataLoaded) {
          this.clearLoaderInterval(intervalId);
        }
      }
    }, 500);

    //clear the interval after 5 seconds to avoid infinite loop in case spinner is not found
    setTimeout(() => {
      this.clearLoaderInterval(intervalId);
    }, 5000);
  }

  clearLoaderInterval(intervalId: any) {
    this.showView = true;
    if (intervalId) {
      clearInterval(intervalId);
    }
  }

  //Get selected tab data for filters
  getParentJSON() {
    this.reportsService.getInbuiltReport(this.resolvedReportName).subscribe((data) => {
      this.handleParentJSONData(data);
    }),
      (error: any) => {
        console.error('Error fetching report data: ', error);
      };
  }

  handleParentJSONData(data: any) {
    try {
      if (data && data.length > 0) {
        const report = data[0];
        this.reportDocumentation = report.Documentation || '';
        let inbuiltReportData = report.inbuilt_report_json ? JSON.parse(report.inbuilt_report_json) : null;
        if (inbuiltReportData && inbuiltReportData.pages) {
          this.isParentReportDataLoaded = true;
          const keys = Object.keys(inbuiltReportData.pages);
          const pagesData: InbuiltReportModel = {
            pages: keys.map((key) => {
              return {
                key: key,
                name: inbuiltReportData.pages[key].name || key,
                order: inbuiltReportData.pages[key].order,
                tabs: inbuiltReportData.pages[key].tabs,
              };
            })
          };
          //sort pages data based on order
          // pagesData.pages.sort((a, b) => a.order - b.order);
          if (pagesData.pages && pagesData.pages.length > 0) {
            pagesData.pages.forEach((page) => {
              if (page.tabs) {
                const tabKeys = Object.keys(page.tabs);
                const sourceTabs = page.tabs as Record<string, any>;
                page.tabs = tabKeys.map((key:string) => {
                  return {
                    key,
                    name: sourceTabs[key]?.name || key,
                    order: sourceTabs[key]?.order,
                    elements: sourceTabs[key]?.elements,
                    documentation: '',
                    class: sourceTabs[key]?.class || '',
                  };
                });
              }
            });
            // set default page as first page in the list
            const index = Object.keys(inbuiltReportData.pages).findIndex(key => key === 'default');
            const currentPage: PageModel | null = pagesData.pages.length > 0 ? pagesData.pages[0] : null;
            const currentTab: TabModel | null = currentPage && currentPage.tabs && currentPage.tabs.length > 0 ? currentPage.tabs[0] : null;
            this.pages = pagesData.pages;
            if (currentPage) {
              this.getChildJSON(currentPage as PageModel, currentTab);
            }
          }
          
        }
      }else{
        this.toastr.error('No data found for the selected report.', 'Error', {
          timeOut: 5000,
        });
       this.showView = true;
      }
    } catch (error) {
      console.error('Error handling in parentJson data: ', error);
    }
  }

  getChildJSON(currentPage: PageModel, currentTab: TabModel | null, tabIndex: number | null = null) {
    // const pageIndex = this.pages.findIndex(page => page.key === currentPage.key);
    // if (pageIndex !== -1) {
    //   const tabPosition = currentTab && this.pages[pageIndex].tabs ? this.pages[pageIndex].tabs.findIndex(tab => tab.key === currentTab.key) : null;
    //   if (tabPosition != null && pageIndex != -1) {
    //     const allElementsFetched = this.pages[pageIndex].tabs[tabPosition].elements.every(ele => ele.isDataFetched === true);
    //     if (allElementsFetched) {
    //       this.getPageConfig();
    //       return;
    //     }
    //   }
    // }
    
    const payload = {
      report: this.resolvedReportName,
      page: currentPage ? currentPage.key : '',
      tab: currentTab ? currentTab.key : '',
    }
         this.reportsService.getInbuiltChildReport(payload).subscribe({
          next: (data) => {
            this.patchParentAndChildJson(data, currentPage, currentTab, tabIndex);
            this.isChildReportDataLoaded = true;
            this.isPageChanging = false;
            this.getPageConfig();
          },
          error: (error: any) => {
            this.isPageChanging = false;
            console.error('Error fetching report data: ', error);
          }
        });
  }

  patchParentAndChildJson(data: any, currentPage: PageModel, currentTab: TabModel | null, tabIndex: number | null) {
    try{
      if(data && data.length > 0){
          const pageIndex = this.pages.findIndex(page => page.key === currentPage.key); 
          if(pageIndex !== -1){
            const tabIndex = currentTab && this.pages[pageIndex].tabs ? this.pages[pageIndex].tabs.findIndex(tab => tab.key === currentTab.key) : null;
            if(tabIndex != null && pageIndex != -1){
              this.pages[pageIndex].tabs[tabIndex].elements.forEach((element:any,index) => {
                element.type = element.type ? element.type.toLowerCase() : '';
                if(element.type == 'header'){
                  if(element.children && element.children.length > 0){
                    element.children.forEach((child:any) => {
                      const childDataIndex = data.findIndex((d:any) => d.element_name === child.name && d.element_type === child.type);
                      if(childDataIndex !== -1){
                        //check navigation link and values link in child data and update it in parent data
                        const child_json = data[childDataIndex].element_json;
                        if(child_json.navigationLink){
                          element.navigationLink = child_json.navigationLink;
                        }
                      } 
                    })
                  }
                  const dataNames = data.filter((d:any)=> d.element_name)
                }
                const elementDataIndex = data.findIndex((d:any) => d.element_name === element.name && d.element_type === element.type && d.Page_name === currentPage.key && d.tab_name === currentTab?.key);
                    if(elementDataIndex !== -1){
                      const parentConfig = element.config || {};
                     
                        const element_json = data[elementDataIndex].element_json;
                      this.pages[pageIndex].tabs[tabIndex].elements[index] = {
                            fields: element_json ? element_json.fields || [] : [],
                            name: data[elementDataIndex].element_name,
                            type: data[elementDataIndex].element_type,
                            order: data[elementDataIndex].element_order || 0,
                            filterList: element_json ? element_json.applied_filter || [] : [],
                            config: element_json ? element_json.config || {} : {},
                            no_filters: element_json ? element_json.no_filter || false : false,
                            // default_selection: element_json.default_selection || [],
                            conditions: element_json ? element_json.conditions || '' : '',
                            defaultQuery: element_json ? element_json.defaultQuery || '' : '',
                            baseWhere: element_json ? element_json.baseWhere || '' : '',
                            class: element.class || '',
                            isAttributeVisibile: element_json ? element_json.isAttributeVisibile || false : false,
                            defaultAttributeSelection: element_json ? element_json.defaultAttributeSelection || [] : [],
                            // columnKey: element_json.columnKey || '',
                            applied_filter: element_json ? element_json.applied_filter || [] : [],
                            valuesLink: element_json ? element_json.valuesLink || {} : {},
                            navigationLink: element_json ? element_json.navigationLink || {} : {},
                            parentConfig: parentConfig || {},
                            // chartConfig: element_json ? element_json.chartConfig || {} : {},
                          }
                      }
                    });
            }
                
              }
            
          }
         
         //FindPage with default key and set it as current page
         if(tabIndex == null){
           const defaultPageIndex = this.pages.findIndex(page => page.key === 'default');
          this.currentPage = defaultPageIndex !== -1 ? this.pages[defaultPageIndex] : this.pages[0];
          this.currentTab = this.currentPage?.tabs && this.currentPage.tabs.length > 0 ? this.currentPage.tabs[0] : null; 
        }else{
          // re-point currentPage to the live pages[] entry so its mutated elements are visible
          const resolved = this.pages.find(p => p.key === currentPage.key);
          if (resolved) this.currentPage = resolved;
          this.currentTab = this.currentPage?.tabs && this.currentPage.tabs.length > 0 ? this.currentPage.tabs[tabIndex] : null;
         }
        //  if(this.currentPage && this.currentTab) {
        //  const  metrixelment = this.currentPage?.tabs?.[0]?.elements?.find((ele:any) => ele.type === 'metrics');
        //  if(metrixelment){ 
        //  const  matrixData = JSON.parse(JSON.stringify(metrixelment));
        //     matrixData.type = 'parent_child_table'
        //   this.currentPage.tabs[0].elements.push(matrixData);

        //   }
        // }
         const cachekey = `${this.resolvedReportName}_${this.currentPage ? this.currentPage.key : ''}_${this.currentTab ? this.currentTab.key : ''}_data_page`;
         const object = {
           currentPage: this.currentPage,
         };
         const tabCacheKey = `${this.resolvedReportName}_${this.currentPage ? this.currentPage.key : ''}_${this.currentTab ? this.currentTab.key : ''}_data_tab`;
         const tabObject = {
           currentTab: this.currentTab,
         };
         this.inbuiltUtilityService.cacheData(tabCacheKey, tabObject);
         this.inbuiltUtilityService.cacheData(cachekey, object);
        this.checkTabLabelEmpty();
        this.checkIfFilterElementExists();
         
    }catch(error){
      console.error('Error converting parent JSON data: ', error);
    }
  }

  checkTabLabelEmpty() {
    if(this.currentPage && this.currentPage?.tabs.length  == 1 && this.currentPage?.tabs[0].name.trim() === 'default') {
      this.isTabLabelEmpty = true;
    }else{
      this.isTabLabelEmpty = false;
    }
  }

  checkIfFilterElementExists() {
    this.searchTriggered = false;
    const isExists = this.currentTab?.elements.some(ele => ele.type === 'filter');
    if(!isExists){
      this.searchTriggered = true;
    }
  }

  setDefaultData() {
    this.currentPage = null;
    this.isCurrentPageDataLoaded = false;
    this.isParentReportDataLoaded = false;
    this.isChildReportDataLoaded = false;
  }

  handleErrors(error: any) {
    console.error('Error: ', error);
    this.toastr.error('An error occurred while fetching data. Please try again later.', 'Error', {
      timeOut: 5000,
    });
  }

  

  onPageChange(page: PageModel, tabIndex: number | null = null) {
    // reference the live pages[] entry so mutations from patchParentAndChildJson are visible on this.currentPage
    this.currentPage = this.pages.find(p => p.key === page.key) ?? page;
    this.currentTab = this.currentPage?.tabs ? this.currentPage.tabs[tabIndex ?? 0] : null;

    const cachekey = `${this.resolvedReportName}_${this.currentPage?.key ?? ''}_${this.currentTab?.key ?? ''}_data_page`;
    const cached = this.inbuiltUtilityService.getCachedData(cachekey);
    if (cached && cached.currentPage && cached.currentPage.tabs) {
      // destroy then recreate widget-elements-row so child components reinit with the cached page's data
      this.isPageChanging = true;
      this.getPageConfig();
      this.checkTabLabelEmpty();
      this.checkIfFilterElementExists();
      setTimeout(() => { this.isPageChanging = false; }, 0);
    } else {
      // set loader before any render so it always appears on cache miss
      this.isPageChanging = true;
      this.getChildJSON(this.currentPage!, this.currentTab, 0);
    }
  }

  onTabChange(tab: any) {
    const tabIndex = tab.index;
    const currentTab = this.currentPage?.tabs ? this.currentPage.tabs[tabIndex] : null;
    
    const cachekey = `${this.resolvedReportName}_${this.currentPage ? this.currentPage.key : ''}_${currentTab ? currentTab.key : ''}_data_page`;
    const object = this.inbuiltUtilityService.getCachedData(cachekey);
    if(object && object.currentPage && object.currentPage.tabs){
      this.currentTab = this.currentPage?.tabs ? this.currentPage.tabs[tab.index] : null;
      this.getPageConfig();
      this.checkTabLabelEmpty();
      this.checkIfFilterElementExists();
    }else{
      this.getChildJSON(this.currentPage!, currentTab, tabIndex);
    }
  }

  getPageConfig(){
    this.pageConfig = {
      currentPage: this.currentPage,
      currentTab: this.currentTab,
      reportNameFromQueryParams: this.resolvedReportName
    }
    this.tableConfig = {
      ...this.pageConfig,
    }
  }

  handleTableAttribute(ev: any){
    this.searchTriggered = ev?.showTable === true;
    this.tableConfig = {
      ...this.pageConfig,
      ...ev
    }
  }


  updatePageDataFromChildComponents(ev: any){
    if(ev && ev.page && ev.tab && ev.element){
      const pageIndex = this.pages.findIndex(page => page.key === ev.page.key);
      if(pageIndex !== -1){
        const tabIndex = this.pages[pageIndex].tabs.findIndex(tab => tab.key === ev.tab.key);
        if(tabIndex !== -1){
          const elementIndex = this.pages[pageIndex].tabs[tabIndex].elements.findIndex(ele => ele.name === ev.element.name);
          if(elementIndex !== -1){
            this.pages[pageIndex].tabs[tabIndex].elements[elementIndex] = ev.data;
          }
        }
      }
    }
  }

  //Navigate to documentation page
  info() {
    if(this.reportDocumentation){
      window.open(this.reportDocumentation, '_blank');
    }
  }

  getElementLayoutClass(element: any, tab: any): string {
    const classValue = element?.class ?? tab?.class;
    return this.resolveResponsiveColClass(classValue);
  }

  shouldAddRowStartPadding(element: any, tab: any, elementIndex: number, elements: any[]): boolean {
    if (!Array.isArray(elements) || elementIndex < 0 || elementIndex >= elements.length) {
      return false;
    }

    const currentSpan = this.resolveElementSpan(elements[elementIndex], tab);
    if (currentSpan >= 12) {
      return false;
    }

    let occupiedInRow = 0;
    for (let i = 0; i <= elementIndex; i++) {
      const span = this.resolveElementSpan(elements[i], tab);

      if (span >= 12) {
        if (i === elementIndex) {
          return false;
        }
        occupiedInRow = 0;
        continue;
      }

      if (occupiedInRow + span > 12) {
        occupiedInRow = 0;
      }

      const isFirstInRenderedRow = occupiedInRow === 0;
      if (i === elementIndex) {
        return !isFirstInRenderedRow;
      }

      occupiedInRow += span;
      if (occupiedInRow >= 12) {
        occupiedInRow = 0;
      }
    }

    return false;
  }

  private resolveElementSpan(element: any, tab: any): number {
    const classValue = element?.class ?? tab?.class;

    if (typeof classValue === 'number' && classValue >= 1 && classValue <= 12) {
      return classValue;
    }

    const trimmed = String(classValue ?? '').trim();
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric) && numeric >= 1 && numeric <= 12) {
      return numeric;
    }

    const responsiveMatch = trimmed.match(/col-(?:md|lg|xl|xxl)-(\d{1,2})/);
    if (responsiveMatch) {
      const responsiveSpan = Number(responsiveMatch[1]);
      if (!Number.isNaN(responsiveSpan) && responsiveSpan >= 1 && responsiveSpan <= 12) {
        return responsiveSpan;
      }
    }

    const baseMatch = trimmed.match(/col-(\d{1,2})/);
    if (baseMatch) {
      const baseSpan = Number(baseMatch[1]);
      if (!Number.isNaN(baseSpan) && baseSpan >= 1 && baseSpan <= 12) {
        return baseSpan;
      }
    }

    return 12;
  }

  trackByTab(_index: number, tab: any): string {
    // page key prefix ensures mat-tabs are recreated when the active page changes
    const pageKey = this.currentPage?.key ?? '';
    return `${pageKey}__${tab?.key ?? String(_index)}`;
  }

  trackByElement(_index: number, element: any): string {
    // page+tab prefix recreates components on page change; same page reuses DOM to avoid the ngFor warning
    const pageKey = this.currentPage?.key ?? '';
    const tabKey = this.currentTab?.key ?? '';
    return `${pageKey}__${tabKey}__${element?.name ?? String(_index)}__${element?.type ?? ''}`;
  }

  getElementWrapperTitle(element: any): string {
    //if starts with { bracket then show display name else show name
    let title = '';
    if (element?.name && String(element.name).trim().startsWith('{')) {
      title = element?.display_name || '';
    }else{
      title = element?.name || '';
    }
   

    return String(title).trim();
  }

  private resolveResponsiveColClass(classValue: any): string {
    if (classValue === null || classValue === undefined || classValue === '') {
      return 'col-12';
    }

    if (typeof classValue === 'number' && this.responsiveColClassMap[classValue]) {
      return this.responsiveColClassMap[classValue];
    }

    const numericClass = Number(String(classValue).trim());
    if (!Number.isNaN(numericClass) && this.responsiveColClassMap[numericClass]) {
      return this.responsiveColClassMap[numericClass];
    }

    if (typeof classValue === 'string' && classValue.trim()) {
      return classValue.trim();
    }

    return 'col-12';
  }

  private rebindResizeObservers(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.isApplyingRowHeights) {
          return;
        }
        this.scheduleRowHeightSync();
      });
    }

    this.resizeObserver.disconnect();
    this.widgetHosts?.forEach((ref: ElementRef<HTMLElement>) => {
      const native = ref?.nativeElement;
      if (native) {
        const observeTarget = (native.firstElementChild as HTMLElement | null) || native;
        this.resizeObserver?.observe(observeTarget);
      }
    });
  }

  private scheduleStabilizedRowSync(): void {
    this.scheduleRowHeightSync();
    this.scheduleRowHeightSync(120);
    this.scheduleRowHeightSync(320);
    this.scheduleRowHeightSync(700);
  }

  private scheduleRowHeightSync(delayMs: number = 0): void {
    if (delayMs > 0) {
      window.setTimeout(() => this.scheduleRowHeightSync(), delayMs);
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      if (this.resizeRafId !== null) {
        cancelAnimationFrame(this.resizeRafId);
      }

      this.resizeRafId = requestAnimationFrame(() => {
        this.resizeRafId = null;
        this.applyEqualHeightsPerRenderedRow();
      });
    });
  }

  private applyEqualHeightsPerRenderedRow(): void {
    const allHosts = (this.widgetHosts?.toArray() || [])
      .map((ref: ElementRef<HTMLElement>) => ref.nativeElement)
      .filter((node: HTMLElement | null) => !!node && node.isConnected) as HTMLElement[];

    if (allHosts.length === 0) {
      return;
    }

    allHosts.forEach((node: HTMLElement) => node.classList.remove('pe-0'));
    this.clearWidgetHeights(allHosts);

    const activeHosts = allHosts.filter((node: HTMLElement) => {
      const rect = node.getBoundingClientRect();
      return rect.width > 0 && this.measureWidgetHeight(node) > 0;
    });

    if (activeHosts.length === 0) {
      this.clearWidgetHeights(allHosts);
      return;
    }

    this.isApplyingRowHeights = true;
    try {
      const rows = new Map<number, HTMLElement[]>();
      activeHosts.forEach((node: HTMLElement) => {
        const top = Math.round(node.getBoundingClientRect().top);
        let matchedTop: number | null = null;
        rows.forEach((_nodes, rowTop) => {
          if (matchedTop === null && Math.abs(rowTop - top) <= this.rowTopTolerance) {
            matchedTop = rowTop;
          }
        });

        const bucketTop = matchedTop === null ? top : matchedTop;
        const rowNodes = rows.get(bucketTop) || [];
        rowNodes.push(node);
        rows.set(bucketTop, rowNodes);
      });

      rows.forEach((rowNodes: HTMLElement[]) => {
        const maxHeight = rowNodes.reduce((maxVal: number, node: HTMLElement) => {
          return Math.max(maxVal, this.measureWidgetHeight(node));
        }, 0);

        const lastNodeInRow = rowNodes.reduce((last: HTMLElement, current: HTMLElement) => {
          const lastRight = last.getBoundingClientRect().right;
          const currentRight = current.getBoundingClientRect().right;
          return currentRight > lastRight ? current : last;
        }, rowNodes[0]);

        rowNodes.forEach((node: HTMLElement) => {
          if (node !== lastNodeInRow) {
            node.classList.add('pe-0');
          }
        });

        if (maxHeight < this.meaningfulHeightPx) {
          return;
        }

        rowNodes.forEach((node: HTMLElement) => {
          node.style.minHeight = `${maxHeight}px`;
        });
      });
    } finally {
      this.isApplyingRowHeights = false;
    }
  }

  private measureWidgetHeight(node: HTMLElement): number {
    const hostRectHeight = Math.ceil(node.getBoundingClientRect().height);
    const hostScrollHeight = Math.ceil(node.scrollHeight || 0);
    const firstChild = node.firstElementChild as HTMLElement | null;
    const childRectHeight = firstChild ? Math.ceil(firstChild.getBoundingClientRect().height) : 0;
    const childScrollHeight = firstChild ? Math.ceil(firstChild.scrollHeight || 0) : 0;

    return Math.max(hostRectHeight, hostScrollHeight, childRectHeight, childScrollHeight, 0);
  }

  private clearWidgetHeights(nodes?: HTMLElement[]): void {
    const targets = nodes || (this.widgetHosts?.toArray() || []).map((ref: ElementRef<HTMLElement>) => ref.nativeElement);
    targets.forEach((node: HTMLElement) => {
      if (node) {
        node.style.minHeight = '';
        node.style.height = 'auto';
      }
    });
  }

  ngOnDestroy(): void {
    if (this.resizeRafId !== null) {
      cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = null;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.destroy$.next();
    this.destroy$.complete();
    this.inbuiltUtilityService.clearAllCachedData();
  }

  navigateToPageOrExternalLink(navigationLink: any) {
    try {
      const obj = Object.keys(navigationLink);
      //validate the navigation is page level or internal level to other page or external level
      const isPageLevel = obj.includes('inReportLink');
      const isInternalLevel = obj.includes('PageLink');
      const isExternalLevel = obj.includes('ExternalLink');
      if (isPageLevel) {
        const pageName = navigationLink.inReportLink.pageName;
        const tabName = navigationLink.inReportLink.tabName;
        const pageIndex = this.pages.findIndex(page => page.key.toLowerCase() === pageName.toLowerCase());
        if (pageIndex !== -1) {
          const tabIndex = this.pages[pageIndex].tabs.findIndex(tab => tab.key.toLowerCase() === tabName.toLowerCase());
          if (tabIndex !== -1) {
            this.currentTab = this.pages[pageIndex].tabs[tabIndex];
            this.onPageChange(this.pages[pageIndex], tabIndex);
          }
        }
      } else if (isInternalLevel || isExternalLevel) {
        const rawLink = navigationLink.pageLink || navigationLink.externalLink;
        const resolvedBaseUrl = this.getBaseUrl();
        const resolvedPath = rawLink.replace(/\{base_url\}/gi, resolvedBaseUrl).trim();
        const params = navigationLink.pageLink?.params || navigationLink.externalLink?.params || {};
        const queryParams = this.buildLinkQueryParams(navigationLink.pageLink || navigationLink.externalLink, {}, '', '');
        const finalUrl = queryParams ? `${resolvedPath}?${queryParams}` : resolvedPath;
        const isNewTab = navigationLink.pageLink?.newBrowserTab || navigationLink.externalLink?.newBrowserTab;
        // Sanitize URL before navigation
        const safeUrl: SafeUrl = this.sanitizer.bypassSecurityTrustUrl(finalUrl);
        if (isNewTab) {
          window.open(String(safeUrl), '_blank');
        } else {
          window.location.href = String(safeUrl);
        }
      }
    } catch (error) {
      console.error('Error navigating to page or external link: ', error);
    }
  }


   private buildLinkQueryParams(linkConfig: any, rowData: any, columnKey: string, cellValue: any): string {
    const paramsConfig = typeof linkConfig === 'object' && linkConfig && !Array.isArray(linkConfig)
      ? (linkConfig.params || {})
      : {};

    const queryParts: string[] = [];
    const appendParam = (paramName: string, value: any) => {
      const cleanParamName = String(paramName ?? '').trim();
      if (!cleanParamName || value === null || value === undefined || value === '') {
        return;
      }

      queryParts.push(`${encodeURIComponent(cleanParamName)}=${encodeURIComponent(String(value))}`);
    };

    Object.keys(paramsConfig || {}).forEach((sourceKey: string) => {
      const targetParamName = paramsConfig[sourceKey];
      const rowValue = Object.prototype.hasOwnProperty.call(rowData || {}, sourceKey)
        ? rowData[sourceKey]
        : (sourceKey === columnKey ? cellValue : undefined);

      appendParam(targetParamName, rowValue);
    });

    return queryParts.join('&');
  }



  private getBaseUrl(): string {
    const fallbackBaseUrl = window?.location?.origin || '';
    return String(fallbackBaseUrl).replace(/\/$/, '');
  }
}
